import { Container, Graphics } from 'pixi.js';
import type { DecorKind } from '@duck/contracts';
import { darken, lighten, mix, type ThemeArt } from './theme-art';
import { AO, bodyRect } from './shading';

/**
 * Level-editor TAG: every catalog piece stamps its {@link DecorKind} (+ any size args) on the returned
 * Container. This is pure metadata — invisible to rendering, harmless in the live game — that lets the
 * editor (a) CAPTURE the office's existing furniture into editable {@link DecorItem} data and (b) SUPPRESS
 * catalog furniture from the decorators so it can re-render it from that data. Bespoke room-local pieces
 * are untagged, so they stay painted backdrop.
 */
export interface DecorTagged {
  __decorKind?: DecorKind;
  __decorArgs?: { w?: number; h?: number };
}
function tag(c: Container, kind: DecorKind, args?: { w?: number; h?: number }): Container {
  (c as Container & DecorTagged).__decorKind = kind;
  if (args) (c as Container & DecorTagged).__decorArgs = args;
  return c;
}

/**
 * The DECORATIVE FURNITURE LIBRARY — a rich set of reusable, procedural scenery builders the per-room
 * modules ({@link './rooms'}) compose from. Each builder returns a Pixi {@link Container} drawn in LOCAL
 * space (footprint centred on its placement point, body rising above it) with a soft contact shadow, so a
 * room module just creates it, sets `.x/.y`, and adds it. Everything is themed off {@link ThemeArt} (a
 * café's warm wood, a station's brushed metal, the academy's brass) and built ONCE on map build/re-skin —
 * never per frame, never allocating in the loop (Performance Engineer gate).
 *
 * Decorative vs interactive: the INTERACTIVE props (coffee machine, workstation, sofa, TV, arcade — the
 * things you press E on) live in {@link './props'} and are placed from the OFFICE_OBJECTS contract. THIS
 * file is pure set-dressing — the desks, chairs, rugs, plants, art and clutter that make a room feel
 * furnished and lived-in. A room agent picks from the catalog below; no item here gates any gameplay.
 *
 * Later (ADR-0010): a commissioned/AI sprite for any of these would replace the body geometry while the
 * contact shadow stays procedural — the placement contract (local-space, centred, shadowed) is untouched.
 *
 * ── CATALOG (one line each so room agents know what's available) ────────────────────────────────────
 *   longDesk(art, w?)        — a wide office desk slab on legs (workbench surface; pair with chairs).
 *   deskChair(art, facing?)  — a simple task chair, seat + low back, optionally facing 'up'|'down'.
 *   officeChair(art)         — a wheeled swivel chair with a tall padded back (exec/desk seating).
 *   sofa(art)                — a plump three-cushion couch (decor twin of the interactive prop sofa).
 *   armchair(art)            — a single soft lounge chair with arms (lounge/reading nooks).
 *   coffeeTable(art)         — a low round table with a soft top sheen (lounge/lobby centrepiece).
 *   rug(art, w, h)           — a soft rectangular area rug with an inset border (anchors a seating group).
 *   monitor(art)             — a desktop monitor on a stand with a lit screen (desk dressing).
 *   computer(art)            — a small desktop tower + monitor combo (a full workstation look, decor).
 *   plant(art, kind?)        — a potted plant; kind 'leafy'|'tall'|'fern' for variety around edges.
 *   bookshelf(art)           — a shelving unit with colourful book spines (libraries/offices).
 *   wallArt(art, kind?)      — a framed painting/poster for a back wall; kind 'painting'|'poster'|'abstract'.
 *   hookah(art)              — a tall shisha pipe with a glowing bowl (the lounge's signature relax prop).
 *   gameConsole(art)         — a console + small screen on a low stand (game-room dressing, decor TV).
 *   whiteboard(art)          — a wall whiteboard on a frame with faint marker scribbles (meeting rooms).
 *   cabinet(art)             — a low storage cabinet / credenza with drawer pulls (offices/kitchens).
 *   lamp(art)                — a floor lamp casting a soft warm glow pool (cosy corners, reading light).
 */

/** A soft elliptical contact shadow so a piece reads as resting on the floor (not floating). */
function contactShadow(w: number, h: number, y = 0): Graphics {
  return new Graphics().ellipse(0, y, w, h).fill({ color: 0x000000, alpha: 0.22 });
}

/** A wide office desk slab on two legs — the workbench surface a room pairs with chairs + monitors. */
export function longDesk(art: ThemeArt, w = 84): Container {
  const c = new Container();
  const wood = mix(0x7a5733, art.wall, 0.25);
  const half = w / 2;
  c.addChild(contactShadow(half + 4, 10, 14));
  c.addChild(
    new Graphics()
      // legs
      .rect(-half + 4, 3, 5, 15)
      .fill(darken(wood, 0.38))
      .rect(half - 9, 3, 5, 15)
      .fill(darken(wood, 0.38))
      // desktop: a darker front face (visible edge thickness) under a lit top surface (key light top-left)
      .roundRect(-half, 1, w, 4, 2)
      .fill(darken(wood, 0.22))
      .roundRect(-half, -6, w, 8, 3)
      .fill(wood)
      .roundRect(-half, -6, w, 2.4, 3)
      .fill({ color: 0xffffff, alpha: 0.16 })
      // AO seam where the top meets the front face
      .roundRect(-half, 0, w, 2, 2)
      .fill({ color: 0x000000, alpha: AO * 0.6 }),
  );
  return tag(c, 'long-desk', { w });
}

/** A task chair: four splayed legs, a contoured backrest with a lumbar accent, a lit seat cushion.
 *  `facing` orients the back: 'up' (back at top/far) or 'down' (back at bottom/near). */
export function deskChair(art: ThemeArt, facing: 'up' | 'down' = 'up'): Container {
  const c = new Container();
  const seat = darken(art.wall, 0.18);
  const frame = darken(seat, 0.32);
  const dir = facing === 'up' ? -1 : 1;
  c.addChild(contactShadow(13, 5, 9));
  const g = new Graphics();
  // Four legs splaying out from the seat — gives a real chair silhouette, not a floating pad.
  g.moveTo(-8, 8).lineTo(-11, 18).moveTo(8, 8).lineTo(11, 18)
    .moveTo(-5, 9).lineTo(-6, 19).moveTo(5, 9).lineTo(6, 19)
    .stroke({ color: frame, width: 2.4 });
  // Backrest toward `facing`: a contoured panel with a lit top edge (key light) + a lumbar accent.
  const by = dir < 0 ? -16 : 10;
  g.roundRect(-9, by, 18, 14, 5).fill(darken(seat, 0.1));
  g.roundRect(-9, by, 18, 4, 5).fill({ color: 0xffffff, alpha: 0.12 });
  g.roundRect(-7, by + 5, 14, 4, 2).fill({ color: art.accent, alpha: 0.3 });
  // Seat cushion with the unified top-left key light.
  bodyRect(g, -10, -3, 20, 12, 5, seat);
  c.addChild(g);
  return tag(c, facing === 'up' ? 'desk-chair-up' : 'desk-chair-down');
}

/** A wheeled swivel chair with a tall padded back — exec / focused-desk seating. */
export function officeChair(art: ThemeArt): Container {
  const c = new Container();
  const pad = mix(0x32384a, art.accent, 0.18);
  c.addChild(contactShadow(13, 5, 12));
  const g = new Graphics();
  // Tall padded back with the unified key light + a lumbar accent band (ergonomic read).
  bodyRect(g, -11, -28, 22, 28, 7, pad);
  g.roundRect(-8, -16, 16, 5, 2).fill({ color: art.accent, alpha: 0.22 });
  // Seat cushion.
  bodyRect(g, -12, -4, 24, 12, 5, lighten(pad, 0.06));
  // Gas post + a proper 5-spoke star base with castors (not two flat lines).
  g.rect(-2, 8, 4, 8).fill(0x2a2f3a);
  for (const [ex, ey] of [
    [-11, 17],
    [0, 18],
    [11, 17],
  ] as const) {
    g.moveTo(0, 12).lineTo(ex, ey).stroke({ color: 0x2a2f3a, width: 2 });
    g.circle(ex, ey, 1.6).fill(0x14171f);
  }
  c.addChild(g);
  return tag(c, 'office-chair');
}

/** A plump three-cushion couch (decorative twin of the interactive prop sofa, same silhouette). */
export function sofa(art: ThemeArt): Container {
  const c = new Container();
  const fabric = mix(0x6a4763, art.accent, 0.2);
  c.addChild(contactShadow(40, 10, 16));
  c.addChild(
    new Graphics()
      .roundRect(-38, -22, 76, 22, 8)
      .fill(darken(fabric, 0.15))
      .roundRect(-40, -6, 80, 18, 8)
      .fill(fabric),
  );
  for (const cx of [-25, 0, 25]) {
    c.addChild(
      new Graphics()
        .roundRect(cx - 12, -8, 24, 14, 5)
        .fill(lighten(fabric, 0.12))
        .roundRect(cx - 12, -8, 24, 5, 5)
        .fill({ color: 0xffffff, alpha: 0.12 }),
    );
  }
  c.addChild(
    new Graphics()
      .roundRect(-44, -14, 10, 26, 5)
      .fill(darken(fabric, 0.08))
      .roundRect(34, -14, 10, 26, 5)
      .fill(darken(fabric, 0.08))
      .roundRect(-34, -6, 14, 12, 4)
      .fill({ color: art.accent, alpha: 0.85 }),
  );
  return tag(c, 'sofa');
}

/** A single soft lounge armchair with arms — reading nooks, lobbies, the lounge. */
export function armchair(art: ThemeArt): Container {
  const c = new Container();
  const fabric = mix(0x5a4a66, art.accent, 0.22);
  c.addChild(contactShadow(20, 7, 12));
  c.addChild(
    new Graphics()
      // back
      .roundRect(-18, -22, 36, 22, 9)
      .fill(darken(fabric, 0.14))
      // seat
      .roundRect(-18, -6, 36, 16, 8)
      .fill(fabric)
      .roundRect(-16, -8, 32, 9, 7)
      .fill(lighten(fabric, 0.12))
      // arms
      .roundRect(-22, -12, 8, 22, 4)
      .fill(darken(fabric, 0.07))
      .roundRect(14, -12, 8, 22, 4)
      .fill(darken(fabric, 0.07)),
  );
  return tag(c, 'armchair');
}

/** A low round coffee table with a soft top sheen — a lounge/lobby seating-group centrepiece. */
export function coffeeTable(art: ThemeArt): Container {
  const c = new Container();
  const top = mix(0x6a4a2b, art.wall, 0.3);
  c.addChild(contactShadow(34, 10, 16));
  // Four little legs angling to the floor — grounds the table instead of a floating disc.
  c.addChild(
    new Graphics()
      .moveTo(-20, 8)
      .lineTo(-24, 18)
      .moveTo(20, 8)
      .lineTo(24, 18)
      .moveTo(-10, 9)
      .lineTo(-12, 19)
      .moveTo(10, 9)
      .lineTo(12, 19)
      .stroke({ color: darken(top, 0.38), width: 2.6 }),
  );
  c.addChild(
    new Graphics()
      .ellipse(0, 8, 30, 13)
      .fill(darken(top, 0.22)) // edge / thickness
      .ellipse(0, 2, 30, 14)
      .fill(top)
      .ellipse(0, 6, 26, 8)
      .fill({ color: 0x000000, alpha: AO * 0.5 }) // AO under the lip
      .ellipse(0, -3, 22, 9)
      .fill({ color: 0xffffff, alpha: 0.12 }), // lit top sheen (key light)
  );
  return tag(c, 'coffee-table');
}

/** A soft rectangular area rug with an inset border — anchors a seating group on the floor. */
export function rug(art: ThemeArt, w: number, h: number): Container {
  const c = new Container();
  const g = new Graphics()
    .roundRect(-w / 2, -h / 2, w, h, 12)
    .fill({ color: art.rug, alpha: 0.6 })
    // a double woven border (a quiet pattern, not a loud blob)
    .roundRect(-w / 2 + 7, -h / 2 + 7, w - 14, h - 14, 8)
    .stroke({ color: art.rugBorder, width: 2, alpha: 0.55 })
    .roundRect(-w / 2 + 12, -h / 2 + 12, w - 24, h - 24, 6)
    .stroke({ color: art.rugBorder, width: 1, alpha: 0.3 });
  // Motif varies by SHAPE so a room's rugs aren't clones: long runners get woven stripes, squarer rugs
  // get a center medallion. Both stay low-alpha — a quiet floor zone, never a loud blob.
  const ratio = Math.max(w, h) / Math.min(w, h);
  if (ratio > 1.7) {
    const along = w >= h;
    const span = (along ? h : w) - 28;
    for (let i = 1; i <= 3; i++) {
      const o = -span / 2 + (span * i) / 4;
      if (along) g.rect(-w / 2 + 14, o - 1.5, w - 28, 3).fill({ color: art.rugBorder, alpha: 0.2 });
      else g.rect(o - 1.5, -h / 2 + 14, 3, h - 28).fill({ color: art.rugBorder, alpha: 0.2 });
    }
  } else {
    const m = Math.min(w, h) * 0.22;
    g.poly([0, -m, m, 0, 0, m, -m, 0]).fill({ color: art.rugBorder, alpha: 0.16 });
    g.poly([0, -m * 0.58, m * 0.58, 0, 0, m * 0.58, -m * 0.58, 0]).stroke({
      color: art.accent,
      width: 1,
      alpha: 0.25,
    });
  }
  c.addChild(g);
  return tag(c, 'rug', { w, h });
}

/** A desktop monitor on a stand with a lit screen — desk dressing (decor, not the interactive desk). */
export function monitor(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(contactShadow(16, 5, 6));
  c.addChild(
    new Graphics()
      // stand
      .rect(-2, -4, 4, 6)
      .fill(0x2a2f3a)
      .roundRect(-9, 1, 18, 3, 1.5)
      .fill(0x2a2f3a)
      // bezel + lit screen
      .roundRect(-15, -24, 30, 20, 2)
      .fill(0x1b1f29)
      .roundRect(-12, -21, 24, 14, 1.5)
      .fill({ color: lighten(art.glow, 0.1), alpha: 0.95 })
      .rect(-9, -18, 12, 1.5)
      .fill({ color: 0xffffff, alpha: 0.35 })
      .rect(-9, -14, 16, 1.5)
      .fill({ color: 0xffffff, alpha: 0.25 })
      // a glass-glare triangle in the top-left corner — the "this screen is glossy" tell (Wave 4)
      .poly([-12, -21, -3, -21, -12, -12])
      .fill({ color: 0xffffff, alpha: 0.1 }),
  );
  return tag(c, 'monitor');
}

/** A small desktop tower + monitor — a full workstation look as pure decor (no interaction). */
export function computer(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(monitor(art));
  // A little tower beside the monitor with a power LED.
  c.addChild(
    new Graphics()
      .roundRect(16, -8, 10, 18, 2)
      .fill(0x23272f)
      .roundRect(16, -8, 10, 3, 2)
      .fill({ color: 0xffffff, alpha: 0.1 })
      .circle(21, -3, 1.4)
      .fill({ color: art.glow, alpha: 0.9 }),
  );
  return tag(c, 'computer');
}

/** A potted plant. `kind`: 'leafy' (broad lobes), 'tall' (slim stalks), 'fern' (low fan). */
export function plant(art: ThemeArt, kind: 'leafy' | 'tall' | 'fern' = 'leafy'): Container {
  const c = new Container();
  const leaf = mix(0x3f9d52, art.accent, 0.12);
  const pot = mix(0x9c5a2e, art.wall, 0.2);
  c.addChild(new Graphics().ellipse(0, 16, 13, 5).fill({ color: 0x000000, alpha: 0.2 }));
  // Terracotta pot (shared across kinds): a tapered body (wide rim → narrow base) with a rim lip, a
  // shaded right flank + a lit rim (key light top-left). Reads as a real pot, not a flat box.
  c.addChild(
    new Graphics()
      .poly([-7, 7, 7, 7, 5, 16, -5, 16])
      .fill(pot)
      .poly([1, 7, 7, 7, 5, 16, 1, 16])
      .fill({ color: darken(pot, 0.18), alpha: 0.55 })
      .roundRect(-8, 3.5, 16, 4.5, 1.5)
      .fill(lighten(pot, 0.1))
      .roundRect(-8, 3.5, 16, 2, 1.5)
      .fill({ color: 0xffffff, alpha: 0.16 }),
  );
  const foliage = new Graphics();
  if (kind === 'tall') {
    // Slim upright stalks (a snake-plant / palm read).
    for (const [x, hgt] of [
      [-5, 30],
      [0, 38],
      [5, 28],
    ] as const) {
      foliage.ellipse(x, 4 - hgt / 2, 4, hgt / 2).fill(x === 0 ? leaf : mix(leaf, 0x000000, 0.1));
    }
  } else if (kind === 'fern') {
    // A low broad fan of fronds.
    for (let i = -2; i <= 2; i++) {
      foliage.ellipse(i * 7, -2, 5, 13).fill(i % 2 ? lighten(leaf, 0.1) : darken(leaf, 0.08));
    }
  } else {
    // Leafy: three soft lobes (the classic potted plant).
    foliage
      .ellipse(0, -10, 12, 18)
      .fill(leaf)
      .ellipse(-9, -2, 9, 13)
      .fill(lighten(leaf, 0.1))
      .ellipse(9, -2, 9, 13)
      .fill(darken(leaf, 0.12));
  }
  c.addChild(foliage);
  return tag(c, kind === 'tall' ? 'plant-tall' : kind === 'fern' ? 'plant-fern' : 'plant-leafy');
}

/** A shelving unit with colourful book spines — libraries, offices, cosy corners. */
export function bookshelf(art: ThemeArt): Container {
  const c = new Container();
  const frame = mix(0x6a4a2b, art.wall, 0.3);
  const g = new Graphics()
    .ellipse(0, 30, 24, 6)
    .fill({ color: 0x000000, alpha: 0.2 })
    .roundRect(-22, -28, 44, 58, 3)
    .fill(frame)
    .roundRect(-22, -28, 44, 3, 3)
    .fill({ color: 0xffffff, alpha: 0.1 });
  const spines = [lighten(art.accent, 0.1), 0xff7a6b, 0x5ad1ff, 0x95e06c, 0xffd23f];
  for (let row = 0; row < 3; row++) {
    const y = -22 + row * 18;
    g.rect(-19, y + 13, 38, 2).fill(darken(frame, 0.4));
    for (let i = 0; i < 7; i++) {
      const h = 11 + (i % 3) * 2;
      g.rect(-18 + i * 5, y + 12 - h, 4, h).fill(spines[(row * 7 + i) % spines.length]!);
    }
  }
  c.addChild(g);
  return tag(c, 'bookshelf');
}

/** A framed wall picture. `kind`: 'painting' (warm scene), 'poster' (bold bands), 'abstract' (shapes). */
export function wallArt(art: ThemeArt, kind: 'painting' | 'poster' | 'abstract' = 'painting'): Container {
  const c = new Container();
  const frame = darken(art.wall, 0.18);
  c.addChild(
    new Graphics()
      .roundRect(-22, -16, 44, 32, 2)
      .fill(frame)
      .roundRect(-22, -16, 44, 32, 2)
      .stroke({ color: lighten(frame, 0.2), width: 1.5, alpha: 0.6 })
      // an inner mat border — the gallery-framed look
      .roundRect(-19, -13, 38, 26, 1.5)
      .stroke({ color: lighten(frame, 0.35), width: 1, alpha: 0.5 }),
  );
  const art2 = new Graphics();
  if (kind === 'poster') {
    // bold colour bands + a little sunburst motif radiating from a disc
    art2
      .rect(-18, -12, 36, 8)
      .fill({ color: art.accent, alpha: 0.85 })
      .rect(-18, -2, 36, 6)
      .fill({ color: lighten(art.glow, 0.1), alpha: 0.8 })
      .rect(-18, 6, 36, 6)
      .fill({ color: darken(art.accent, 0.2), alpha: 0.8 });
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      art2.moveTo(0, -8).lineTo(Math.cos(a) * 9, -8 + Math.sin(a) * 9);
    }
    art2.stroke({ color: 0xfff3c4, width: 1, alpha: 0.5 }).circle(0, -8, 3).fill({ color: 0xfff3c4, alpha: 0.9 });
  } else if (kind === 'abstract') {
    // a richer composition: three shapes + a connecting line
    art2
      .rect(-18, -12, 36, 24)
      .fill({ color: lighten(art.windowSky, 0.05), alpha: 0.7 })
      .circle(-6, -2, 7)
      .fill({ color: art.accent, alpha: 0.85 })
      .poly([6, 10, 16, -10, 18, 10])
      .fill({ color: lighten(art.glow, 0.1), alpha: 0.8 })
      .roundRect(-16, 4, 7, 7, 1.5)
      .fill({ color: darken(art.accent, 0.15), alpha: 0.8 })
      .moveTo(-6, -2)
      .lineTo(12, 2)
      .stroke({ color: 0xffffff, width: 1, alpha: 0.4 });
  } else {
    // painting: a soft horizon — sky, a sun, a little cloud, two layered hills.
    art2
      .rect(-18, -12, 36, 24)
      .fill({ color: lighten(art.windowSky, 0.08), alpha: 0.8 })
      .circle(8, -5, 4)
      .fill({ color: 0xfff0c0, alpha: 0.9 })
      .ellipse(-6, -7, 7, 2.4)
      .fill({ color: 0xffffff, alpha: 0.5 })
      .rect(-18, 0, 36, 12)
      .fill({ color: mix(art.floorA, 0x3f9d52, 0.5), alpha: 0.7 })
      .rect(-18, 4, 36, 8)
      .fill({ color: mix(art.floorA, 0x3f9d52, 0.4), alpha: 0.85 });
  }
  c.addChild(art2);
  return tag(c, kind === 'poster' ? 'wall-poster' : kind === 'abstract' ? 'wall-abstract' : 'wall-painting');
}

/** A tall shisha / hookah pipe with a glowing bowl — the lounge's signature relax prop (decor). */
export function hookah(art: ThemeArt): Container {
  const c = new Container();
  const metal = mix(0x8a6a3a, art.accent, 0.25);
  c.addChild(contactShadow(12, 5, 18));
  c.addChild(
    new Graphics()
      // base bowl (water jar)
      .roundRect(-9, 2, 18, 16, 6)
      .fill(mix(0x3a4f6a, art.accent, 0.2))
      .roundRect(-9, 2, 18, 5, 6)
      .fill({ color: 0xffffff, alpha: 0.18 })
      // stem
      .rect(-2, -22, 4, 24)
      .fill(metal)
      // tray
      .roundRect(-7, -24, 14, 4, 2)
      .fill(darken(metal, 0.2))
      // glowing bowl on top
      .roundRect(-5, -34, 10, 10, 3)
      .fill(darken(metal, 0.1))
      .circle(0, -30, 3)
      .fill({ color: 0xff7a4a, alpha: 0.85 }),
  );
  // hose looping off the side
  c.addChild(
    new Graphics()
      .moveTo(2, -10)
      .bezierCurveTo(20, -6, 22, 8, 12, 14)
      .stroke({ color: mix(0x4a3a2a, art.accent, 0.2), width: 3, alpha: 0.9 }),
  );
  return tag(c, 'hookah');
}

/** A games console + small screen on a low stand — game-room dressing (a decorative TV setup). */
export function gameConsole(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(contactShadow(28, 8, 22));
  c.addChild(
    new Graphics()
      // low stand
      .roundRect(-28, 12, 56, 10, 3)
      .fill(darken(art.wall, 0.18))
      .roundRect(-28, 12, 56, 3, 3)
      .fill({ color: 0xffffff, alpha: 0.1 })
      // screen showing a game
      .roundRect(-26, -22, 52, 32, 3)
      .fill(0x14171f)
      .roundRect(-23, -19, 46, 26, 2)
      .fill({ color: lighten(art.glow, 0.15), alpha: 0.9 })
      .circle(-8, -6, 3)
      .fill(0xff5d8f)
      .circle(8, -6, 3)
      .fill(0x5ad1ff)
      // console box + a tiny LED
      .roundRect(-12, 13, 24, 6, 1.5)
      .fill(0x23272f)
      .circle(8, 16, 1.2)
      .fill({ color: art.glow, alpha: 0.9 }),
  );
  return tag(c, 'game-console');
}

/** A wall whiteboard on a frame with faint marker scribbles — meeting rooms, planning corners. */
export function whiteboard(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(
    new Graphics()
      // tray + board
      .roundRect(-55, 18, 110, 4, 2)
      .fill(art.baseboard)
      .roundRect(-53, -20, 106, 38, 3)
      .fill(0xf4f4f8)
      .roundRect(-53, -20, 106, 38, 3)
      .stroke({ color: darken(art.wall, 0.2), width: 2, alpha: 0.7 }),
  );
  // Believable board content: a title bar + underline, two bullet rows, a little bar chart.
  c.addChild(
    new Graphics()
      .roundRect(-46, -14, 34, 3, 1.5)
      .fill({ color: art.accent, alpha: 0.85 })
      .moveTo(-46, -9)
      .lineTo(-4, -9)
      .stroke({ color: darken(art.wall, 0.1), width: 1, alpha: 0.4 })
      .circle(-44, -3, 1.4)
      .fill({ color: 0xff5d5d, alpha: 0.7 })
      .circle(-44, 4, 1.4)
      .fill({ color: 0x5ad1ff, alpha: 0.8 })
      .moveTo(-40, -3)
      .lineTo(-8, -3)
      .moveTo(-40, 4)
      .lineTo(-16, 4)
      .stroke({ color: art.accent, width: 2, alpha: 0.5 })
      // a tiny bar chart, bottom-right
      .moveTo(8, 12)
      .lineTo(48, 12)
      .stroke({ color: darken(art.wall, 0.2), width: 1, alpha: 0.5 })
      .rect(12, 2, 5, 10)
      .fill({ color: 0x95e06c, alpha: 0.85 })
      .rect(20, -3, 5, 15)
      .fill({ color: 0x5ad1ff, alpha: 0.85 })
      .rect(28, -8, 5, 20)
      .fill({ color: 0xffd23f, alpha: 0.85 })
      .rect(36, -12, 5, 24)
      .fill({ color: 0xff7a6b, alpha: 0.85 }),
  );
  // A sticky note in the corner for warmth.
  c.addChild(
    new Graphics()
      .rect(33, -16, 14, 13)
      .fill({ color: 0xffe08a, alpha: 0.95 })
      .rect(33, -16, 14, 3)
      .fill({ color: 0xffd23f, alpha: 0.6 }),
  );
  return tag(c, 'whiteboard');
}

/** A low storage cabinet / credenza with drawer pulls — offices, kitchens, reception backs. */
export function cabinet(art: ThemeArt): Container {
  const c = new Container();
  const wood = mix(0x6a4a2b, art.wall, 0.3);
  c.addChild(contactShadow(34, 8, 18));
  const g = new Graphics();
  bodyRect(g, -34, -12, 68, 30, 4, wood);
  // Two drawer faces with pulls.
  for (const dx of [-17, 17]) {
    g.roundRect(dx - 14, -6, 28, 18, 3)
      .fill(darken(wood, 0.12))
      .circle(dx, 3, 2)
      .fill(lighten(art.accent, 0.1));
  }
  c.addChild(g);
  return tag(c, 'cabinet');
}

/** A floor lamp casting a soft warm glow pool — cosy corners, reading light, evening warmth. */
export function lamp(art: ThemeArt): Container {
  const c = new Container();
  c.addChild(contactShadow(10, 4, 22));
  // Soft glow pool on the floor (the lamp's light).
  c.addChild(
    new Graphics()
      .ellipse(0, 18, 26, 12)
      .fill({ color: art.glow, alpha: 0.12 * art.glowStrength + 0.06 }),
  );
  c.addChild(
    new Graphics()
      // pole + base
      .rect(-1.5, -18, 3, 40)
      .fill(darken(art.wall, 0.25))
      .ellipse(0, 22, 8, 3)
      .fill(darken(art.wall, 0.3))
      // shade with a lit underside
      .poly([-13, -18, 13, -18, 9, -32, -9, -32])
      .fill(mix(0xf4e8c8, art.accent, 0.15))
      .rect(-12, -19, 24, 3)
      .fill({ color: art.glow, alpha: 0.85 }),
  );
  return tag(c, 'lamp');
}
